import { useRef, useState, useEffect } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Menu, X, ChevronDown } from 'lucide-react'
import LanguageToggle from './LanguageToggle'

import { useTranslation } from 'react-i18next'

gsap.registerPlugin(ScrollTrigger)

const NAV_LINKS = [
  { label: 'nav.home', to: '/' },
  {
    label: 'nav.history',
    to: '/history',
    children: [
      { label: 'nav.overview', to: '/history' },
      { label: 'nav.legends', to: '/history#legends' },
      { label: 'nav.administration', to: '/history#admin' },
    ],
  },
  { label: 'nav.festivals', to: '/festivals' },
  {
    label: 'nav.worship',
    to: '/pooja',
    children: [
      { label: 'nav.poojaTimings', to: '/pooja#timings' },
      { label: 'nav.bookPooja', to: '/pooja' },
      { label: 'nav.fees', to: '/pooja#fees' },
    ],
  },
  { label: 'nav.donate', to: '/donations' },
  { label: 'nav.gallery', to: '/gallery' },
  { label: 'nav.location', to: '/location' },
]

export default function Navbar() {
  const { t } = useTranslation()
  const navRef = useRef(null)
  const drawerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)

  useGSAP(() => {
    ScrollTrigger.create({
      start: 80,
      onEnter: () => setScrolled(true),
      onLeaveBack: () => setScrolled(false),
    })
  }, { scope: navRef })

  useGSAP(() => {
    if (!drawerRef.current) return
    if (open) {
      gsap.fromTo(drawerRef.current,
        { x: '100%', opacity: 0 },
        { x: '0%', opacity: 1, duration: 0.35, ease: 'power2.out' }
      )
    } else {
      gsap.to(drawerRef.current, { x: '100%', opacity: 0, duration: 0.3, ease: 'power2.in' })
    }
  }, [open])

  function handleDropdownToggle(label) {
    setActiveDropdown(activeDropdown === label ? null : label)
  }

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'shadow-lg' : 'bg-saffron'
        }`}
        style={scrolled ? { background: 'linear-gradient(135deg, #1A0A00, #2D1500)' } : {}}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="/galary/logo2.jpg"
                alt="Temple Logo"
                className="h-10 w-10 rounded-full object-cover ring-2 ring-gold group-hover:scale-110 transition-transform"
              />
              <span className="font-tamil text-white font-bold text-base leading-tight">
                ஸ்ரீ லட்சுமி நரசிம்ஹ<br />
                <span className="text-gold text-sm font-normal">பெருமாள் ஆலயம் · கெங்கநல்லூர்</span>
              </span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.children ? (
                    <>
                      <button
                        className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium px-3 py-2 rounded-md hover:bg-white/10 transition-all"
                      >
                        {t(link.label)}
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${activeDropdown === link.label ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {/* Invisible bridge fills the gap so mouse can travel to the menu */}
                      <div className="absolute top-full left-0 right-0 h-2" />

                      <div
                        className="absolute left-0 z-50 pt-2"
                        style={{
                          top: '100%',
                          transition: 'opacity 200ms ease, transform 200ms ease',
                          opacity: activeDropdown === link.label ? 1 : 0,
                          transform: activeDropdown === link.label ? 'translateY(0) scaleY(1)' : 'translateY(-6px) scaleY(0.95)',
                          transformOrigin: 'top center',
                          pointerEvents: activeDropdown === link.label ? 'auto' : 'none',
                        }}
                      >
                        <div className="bg-white rounded-xl shadow-2xl py-2 min-w-48 border border-gold/20 overflow-hidden">
                          {link.children.map((child) => (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              onClick={() => setActiveDropdown(null)}
                              className={({ isActive }) =>
                                `block px-4 py-2.5 text-sm font-medium transition-all duration-150 ${
                                  isActive
                                    ? 'bg-orange-50 text-saffron border-l-2 border-saffron'
                                    : 'text-charcoal hover:bg-orange-50 hover:text-saffron hover:pl-5'
                                }`
                              }
                            >
                              {t(child.label)}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <NavLink
                      to={link.to}
                      end={link.to === '/'}
                      className={({ isActive }) =>
                        `text-sm font-medium px-3 py-2 rounded-md transition-all ${
                          isActive
                            ? 'text-gold border-b-2 border-gold'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                        }`
                      }
                    >
                      {t(link.label)}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-3">
              <LanguageToggle />
              <Link
                to="/pooja"
                className="bg-white text-saffron font-semibold text-sm px-5 py-2 rounded-full hover:bg-cream transition-all shadow"
              >
                {t('nav.bookPooja')}
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full w-72 bg-charcoal z-100 translate-x-full shadow-2xl lg:hidden"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <span className="text-white font-display font-bold">Menu</span>
          <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
            <X size={22} />
          </button>
        </div>
        <div className="p-5 space-y-1 overflow-y-auto">
          {NAV_LINKS.map((link) => (
            <div key={link.label}>
              <NavLink
                to={link.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-saffron text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                {t(link.label)}
              </NavLink>
              {link.children?.map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to}
                  onClick={() => setOpen(false)}
                  className="block px-8 py-2 text-xs text-white/50 hover:text-gold transition-colors"
                >
                  › {t(child.label)}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
            <LanguageToggle dark />
            <Link
              to="/pooja"
              onClick={() => setOpen(false)}
              className="bg-saffron text-white font-semibold text-sm px-5 py-2.5 rounded-full text-center"
            >
              {t('nav.bookPooja')}
            </Link>
          </div>
        </div>
      </div>

      {/* Drawer backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-90 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  )
}
